import {
	Material,
	ProductId,
	Property,
} from "@animech/axiom-format/src/definitions/definition-bindings";

export interface CustomCameraSettings {
	maxAzimuth?: number;
	minAzimuth?: number;
	minX?: number;
	maxX?: number;
	minY?: number;
	maxY?: number;
	minZ?: number;
	maxZ?: number;
}

export type TableType =
	| "Table-Bovik-150"
	| "Table-B31-84"
	| "Table-B31-170"
	| "Table-B31-230"
	| "Table-6B-60"
	| "Table-9A-100"
	| "Table-9A-120"
	| "Table-B25A-120"
	| "Table-HighTech-60"
	| "Table-HighTech-110"
	| "Table-Libelle-140"
	| "Table-Libelle-70";

export type RoundTableType =
	| "Table-6B-60"
	| "Table-9A-100"
	| "Table-9A-120"
	| "Table-HighTech-60";

// ---------------------------------------------------------------------------
// Side types — pure type + constant definitions (no dependency on root)
// ---------------------------------------------------------------------------

export type RectSide = "top" | "bottom" | "left" | "right";
export type Side = RectSide | "round";

export const RECT_SIDES: readonly RectSide[] = [
	"top",
	"bottom",
	"left",
	"right",
] as const;

export const ALL_SIDES: readonly Side[] = [
	"top",
	"bottom",
	"left",
	"right",
	"round",
] as const;

export const SIDE_INDICES = {
	top: 0,
	bottom: 1,
	left: 2,
	right: 3,
	round: 4,
} as const;

export const SIDE_COUNT = 5;

export type SideIndex = (typeof SIDE_INDICES)[Side];

export function isRoundTable(table: TableType): table is RoundTableType {
	return (
		table === "Table-6B-60" ||
		table === "Table-9A-100" ||
		table === "Table-9A-120" ||
		table === "Table-HighTech-60"
	);
}

export type ChairType =
	| "Chair-A2"
	| "Chair-HighTech"
	| "Chair-Bovik"
	| "Chair-Libelle"
	| "Chair-1";

// Must match ChairType union members exactly.
// If a new chair is added to the union, add it here too —
// Record<ChairType, ...> tables will fail to compile if omitted.
export const chairTypeUniverse: ChairType[] = [
	"Chair-A2",
	"Chair-HighTech",
	"Chair-Bovik",
	"Chair-Libelle",
	"Chair-1",
];

export type BenchType = "Bench-8" | "Bench-9" | "Bench-Libelle";

export type SofaType = "Sofa-3" | "Sofa-5";

export type SeatingType = ChairType | BenchType | SofaType;

// Must match TableType union members exactly.
// Record<TableType, ...> tables will fail to compile if omitted.
export const tableTypeUniverse: TableType[] = [
	"Table-Bovik-150",
	"Table-B31-84",
	"Table-B31-170",
	"Table-B31-230",
	"Table-6B-60",
	"Table-9A-100",
	"Table-9A-120",
	"Table-B25A-120",
	"Table-HighTech-60",
	"Table-HighTech-110",
	"Table-Libelle-140",
	"Table-Libelle-70",
];

// Compile-time exhaustiveness check — will error if a TableType is missing.
const _tableTypeCheck: Record<TableType, true> = {
	"Table-Bovik-150": true,
	"Table-B31-84": true,
	"Table-B31-170": true,
	"Table-B31-230": true,
	"Table-6B-60": true,
	"Table-9A-100": true,
	"Table-9A-120": true,
	"Table-B25A-120": true,
	"Table-HighTech-60": true,
	"Table-HighTech-110": true,
	"Table-Libelle-140": true,
	"Table-Libelle-70": true,
};
void _tableTypeCheck;

export const seatingTypeUniverse: SeatingType[] = [
	"Chair-A2",
	"Chair-HighTech",
	"Chair-Bovik",
	"Chair-Libelle",
	"Chair-1",
	"Bench-8",
	"Bench-9",
	"Bench-Libelle",
	"Sofa-3",
	"Sofa-5",
];

export function isBenchOrSofa(s: SeatingType): s is BenchType | SofaType {
	return (
		s === "Bench-8" ||
		s === "Bench-9" ||
		s === "Bench-Libelle" ||
		s === "Sofa-3" ||
		s === "Sofa-5"
	);
}

// ---------------------------------------------------------------------------
// Viewer mode — first-class configurator state for single-product rendering
// at /#view?article=<SKU>. See docs/adr/0002-viewer-mode-extends-configurator-
// data-model.md.
// ---------------------------------------------------------------------------

export type ViewerMode =
	| "off"
	| "single-table"
	| "single-chair"
	| "single-bench"
	| "single-sofa";

export const viewerModeUniverse: ViewerMode[] = [
	"off",
	"single-table",
	"single-chair",
	"single-bench",
	"single-sofa",
];

export function viewerModeForSeating(s: SeatingType): ViewerMode {
	if (s === "Bench-8" || s === "Bench-9" || s === "Bench-Libelle") {
		return "single-bench";
	}
	if (s === "Sofa-3" || s === "Sofa-5") {
		return "single-sofa";
	}
	return "single-chair";
}

export type Vec3 = { x: number; y: number; z: number };

export type ChairSlot = {
	visible: boolean;
	pos: Vec3;
	rot: Vec3;
	chairType: SeatingType;

	chairMaterialWood: Material | undefined;
	chairMaterialMetal: Material;
	chairMaterialWeld: Material;
	chairMaterialEndcap: Material | undefined;
	chairMaterialScrewAtlas: Material;
	chairMaterialScrewPhillips: Material;

	sideId: Side;
	count: number;
	chairTheme: MaterialTheme;
};

export type Product = {
	type: ProductId;
	color: Property;
};

export type MaterialOptions = {
	metal: Material[];
	wood: Material[];
	excludedCombos?: { wood: Material; metal: Material }[];
};

export function isComboExcluded(
	opts: MaterialOptions,
	wood: Material | undefined,
	metal: Material,
): boolean {
	if (!opts.excludedCombos) {
		return false;
	}
	for (const e of opts.excludedCombos) {
		if (e.wood === wood && e.metal === metal) {
			return true;
		}
	}
	return false;
}

export function getFilteredDomains(
	opts: MaterialOptions,
	currentWood: Material | undefined,
	currentMetal: Material,
): { wood: Material[]; metal: Material[] } {
	if (!opts.excludedCombos || opts.excludedCombos.length === 0) {
		return { wood: opts.wood, metal: opts.metal };
	}
	const wood: Material[] = [];
	for (const w of opts.wood) {
		if (!isComboExcluded(opts, w, currentMetal)) {
			wood.push(w);
		}
	}
	const metal: Material[] = [];
	for (const m of opts.metal) {
		if (!isComboExcluded(opts, currentWood, m)) {
			metal.push(m);
		}
	}
	return { wood, metal };
}

export const woodMaterials = [
	"WoodPineOiled",
	"WoodOakOiled",
	"WoodTeakUntreated",
	"PaintWoodWhite",
	"PaintWoodGrey",
	"PaintWoodGraphite",
	"PaintWoodBlack",
	"PaintWoodRed",
	"PaintWoodGreen",
	"LacquerWhite",
	"LacquerGrey",
	"LacquerGraphite",
	"LacquerBlack",

	"LaminateWhite",
	"LaminateGrey",
	"LaminateGraphite",
	"LaminateBlack",
] as const;

export const metalMaterials = [
	"PaintMetalWhite",
	"PaintMetalGrey",
	"PaintMetalGraphite",
	"PaintMetalBlack",
	"PaintMetalGreen",
	"PaintMetalRed",
	"PaintMetalPowdercoatBlack",
	"PaintMetalPowdercoatChestnut",
	"PaintMetalPowdercoatIvory",
	"PaintMetalPowdercoatPetrol",
	"MetalGalvanizedSteel",
	"MetalGalvanizedZinc",
] as const;

// ---------------------------------------------------------------------------
// Material universe — canonical superset of all material values used in
// $universe arrays across property-definitions.ts. Single source of truth.
// ---------------------------------------------------------------------------

/* eslint-disable @animech/axiom/no-references-in-top-level-values */
export const materialUniverse: (Material | undefined)[] = [
	undefined,
	"WoodPineOiled",
	"WoodPineOiled-Endcap",
	"WoodOakOiled",
	"WoodOakOiled-Endcap",
	"WoodTeakUntreated",
	"WoodTeakUntreated-Endcap",
	"LacquerWhite",
	"LacquerGrey",
	"LacquerGraphite",
	"LacquerBlack",
	"PaintWoodWhite",
	"PaintWoodWhite-Endcap",
	"PaintWoodGrey",
	"PaintWoodGrey-Endcap",
	"PaintWoodGraphite",
	"PaintWoodGraphite-Endcap",
	"PaintWoodBlack",
	"PaintWoodBlack-Endcap",
	"PaintWoodRed",
	"PaintWoodRed-Endcap",
	"PaintWoodGreen",
	"PaintWoodGreen-Endcap",
	"LaminateWhite",
	"LaminateGrey",
	"LaminateGraphite",
	"LaminateBlack",
	"PaintMetalWhite",
	"PaintMetalWhite-Weld",
	"PaintMetalGrey",
	"PaintMetalGrey-Weld",
	"PaintMetalGraphite",
	"PaintMetalGraphite-Weld",
	"PaintMetalBlack",
	"PaintMetalBlack-Weld",
	"PaintMetalGreen",
	"PaintMetalGreen-Weld",
	"PaintMetalRed",
	"PaintMetalRed-Weld",
	"PaintMetalPowdercoatBlack",
	"PaintMetalPowdercoatBlack-Weld",
	"PaintMetalPowdercoatChestnut",
	"PaintMetalPowdercoatChestnut-Weld",
	"PaintMetalPowdercoatIvory",
	"PaintMetalPowdercoatIvory-Weld",
	"PaintMetalPowdercoatPetrol",
	"PaintMetalPowdercoatPetrol-Weld",
	"MetalGalvanizedSteel",
	"MetalGalvanizedSteel-Weld",
	"MetalGalvanizedZinc",
	"MetalGalvanizedZinc-Weld",
	"AtlasScrews",
	"AtlasScrews-Black",
	"ScrewRoundedPhillips-M3x10mm-A",
	"ScrewRoundedPhillips-M3x10mm-A-Black",
];
/* eslint-enable @animech/axiom/no-references-in-top-level-values */

// ---------------------------------------------------------------------------
// Metal projection — maps table-only metals to chair equivalents
// ---------------------------------------------------------------------------

/* eslint-disable @animech/axiom/no-references-in-top-level-values */
export const metalProjection: Partial<Record<Material, Material>> = {
	PaintMetalBlack: "PaintMetalPowdercoatBlack" as Material,
};
/* eslint-enable @animech/axiom/no-references-in-top-level-values */

export function resolveProjectedMetal(
	metal: Material,
	opts: MaterialOptions,
): Material | undefined {
	if (opts.metal.includes(metal)) {
		return metal;
	}
	const projected = metalProjection[metal];
	return projected && opts.metal.includes(projected) ? projected : undefined;
}

// ---------------------------------------------------------------------------
// Material theme — master color selection for the whole configuration
// ---------------------------------------------------------------------------
export type MaterialTheme =
	| "theme-none"
	| "theme-teak-galvanized"
	| "theme-teak-black"
	| "theme-oak-galvanized"
	| "theme-oak-black"
	| "theme-pine-green"
	| "theme-white-lacquer-galvanized"
	| "theme-white-lacquer-black"
	| "theme-green-galvanized"
	| "theme-green-black"
	| "theme-red-galvanized"
	| "theme-red-black"
	| "theme-hightech-white-galvanized"
	| "theme-hightech-white-black"
	| "theme-hightech-graphite-galvanized"
	| "theme-hightech-graphite-black"
	| "theme-hightech-galvanized"
	| "theme-hightech-powdercoat-black"
	| "theme-hightech-powdercoat-chestnut"
	| "theme-hightech-powdercoat-ivory"
	| "theme-hightech-powdercoat-petrol"
	| "theme-libelle-green"
	| "theme-libelle-grey"
	| "theme-libelle-graphite";

export const materialThemeUniverse: MaterialTheme[] = [
	"theme-none",
	"theme-teak-galvanized",
	"theme-teak-black",
	"theme-oak-galvanized",
	"theme-oak-black",
	"theme-pine-green",
	"theme-white-lacquer-galvanized",
	"theme-white-lacquer-black",
	"theme-green-galvanized",
	"theme-green-black",
	"theme-red-galvanized",
	"theme-red-black",
	"theme-hightech-white-galvanized",
	"theme-hightech-white-black",
	"theme-hightech-graphite-galvanized",
	"theme-hightech-graphite-black",
	"theme-hightech-galvanized",
	"theme-hightech-powdercoat-black",
	"theme-hightech-powdercoat-chestnut",
	"theme-hightech-powdercoat-ivory",
	"theme-hightech-powdercoat-petrol",
	"theme-libelle-green",
	"theme-libelle-grey",
	"theme-libelle-graphite",
];

export type MaterialThemeDefinition = {
	wood: Material | undefined;
	metal: Material;
};

export const materialThemes: Record<MaterialTheme, MaterialThemeDefinition> = {
	"theme-none": { wood: undefined, metal: "MetalGalvanizedSteel" },
	"theme-teak-galvanized": {
		wood: "WoodTeakUntreated",
		metal: "MetalGalvanizedSteel",
	},
	"theme-teak-black": {
		wood: "WoodTeakUntreated",
		metal: "PaintMetalBlack",
	},
	"theme-oak-galvanized": {
		wood: "WoodOakOiled",
		metal: "MetalGalvanizedSteel",
	},
	"theme-oak-black": { wood: "WoodOakOiled", metal: "PaintMetalBlack" },
	"theme-pine-green": {
		wood: "WoodPineOiled",
		metal: "PaintMetalGreen",
	},
	"theme-white-lacquer-galvanized": {
		wood: "LacquerWhite",
		metal: "MetalGalvanizedSteel",
	},
	"theme-white-lacquer-black": {
		wood: "LacquerWhite",
		metal: "PaintMetalBlack",
	},
	"theme-green-galvanized": {
		wood: "PaintWoodGreen",
		metal: "MetalGalvanizedSteel",
	},
	"theme-green-black": { wood: "PaintWoodGreen", metal: "PaintMetalBlack" },
	"theme-red-galvanized": {
		wood: "PaintWoodRed",
		metal: "MetalGalvanizedSteel",
	},
	"theme-red-black": { wood: "PaintWoodRed", metal: "PaintMetalBlack" },
	"theme-hightech-white-galvanized": {
		wood: "LaminateWhite",
		metal: "MetalGalvanizedSteel",
	},
	"theme-hightech-white-black": {
		wood: "LaminateWhite",
		metal: "PaintMetalBlack",
	},
	"theme-hightech-graphite-galvanized": {
		wood: "LaminateGraphite",
		metal: "MetalGalvanizedSteel",
	},
	"theme-hightech-graphite-black": {
		wood: "LaminateGraphite",
		metal: "PaintMetalBlack",
	},
	"theme-hightech-galvanized": {
		wood: undefined,
		metal: "MetalGalvanizedSteel",
	},
	"theme-hightech-powdercoat-black": {
		wood: undefined,
		metal: "PaintMetalPowdercoatBlack",
	},
	"theme-hightech-powdercoat-chestnut": {
		wood: undefined,
		metal: "PaintMetalPowdercoatChestnut",
	},
	"theme-hightech-powdercoat-ivory": {
		wood: undefined,
		metal: "PaintMetalPowdercoatIvory",
	},
	"theme-hightech-powdercoat-petrol": {
		wood: undefined,
		metal: "PaintMetalPowdercoatPetrol",
	},
	"theme-libelle-green": {
		wood: undefined,
		metal: "PaintMetalGreen",
	},
	"theme-libelle-grey": {
		wood: undefined,
		metal: "PaintMetalGrey",
	},
	"theme-libelle-graphite": {
		wood: undefined,
		metal: "PaintMetalGraphite",
	},
};

export const defaultThemeForTable: Record<TableType, MaterialTheme> = {
	"Table-B31-84": "theme-teak-galvanized",
	"Table-B31-170": "theme-teak-galvanized",
	"Table-B31-230": "theme-teak-galvanized",
	"Table-6B-60": "theme-teak-galvanized",
	"Table-9A-100": "theme-teak-galvanized",
	"Table-9A-120": "theme-teak-galvanized",
	"Table-B25A-120": "theme-teak-galvanized",
	"Table-HighTech-60": "theme-hightech-white-galvanized",
	"Table-HighTech-110": "theme-hightech-white-galvanized",
	"Table-Libelle-140": "theme-libelle-green",
	"Table-Libelle-70": "theme-libelle-green",
	"Table-Bovik-150": "theme-teak-galvanized",
};

export const defaultThemeForSeating: Record<SeatingType, MaterialTheme> = {
	"Chair-A2": "theme-teak-galvanized",
	"Chair-HighTech": "theme-hightech-galvanized",
	"Chair-Bovik": "theme-teak-galvanized",
	"Chair-Libelle": "theme-libelle-green",
	"Chair-1": "theme-teak-galvanized",
	"Bench-8": "theme-teak-galvanized",
	"Bench-9": "theme-teak-galvanized",
	"Bench-Libelle": "theme-libelle-green",
	"Sofa-3": "theme-teak-galvanized",
	"Sofa-5": "theme-teak-galvanized",
};

export function isThemeValidForProduct(
	def: MaterialThemeDefinition,
	opts: MaterialOptions,
): boolean {
	if (!opts.metal.includes(def.metal)) {
		return false;
	}
	if (opts.wood.length > 0) {
		if (def.wood === undefined || !opts.wood.includes(def.wood)) {
			return false;
		}
	} else {
		if (def.wood !== undefined) {
			return false;
		}
	}
	if (isComboExcluded(opts, def.wood, def.metal)) {
		return false;
	}
	return true;
}

export function getValidThemes(
	activeProducts: MaterialOptions[],
): MaterialTheme[] {
	const valid: MaterialTheme[] = ["theme-none"];
	for (const theme of materialThemeUniverse) {
		if (theme === "theme-none") {
			continue;
		}
		const def = materialThemes[theme];
		let validForAll = true;
		for (const opts of activeProducts) {
			if (!isThemeValidForProduct(def, opts)) {
				validForAll = false;
				break;
			}
		}
		if (validForAll) {
			valid.push(theme);
		}
	}
	return valid;
}

// ---------------------------------------------------------------------------
// Theme projection — lenient compatibility for master broadcast to wood-less
// products (see theme-projection-prd.md §5.1)
// ---------------------------------------------------------------------------

/**
 * Lenient check: theme is compatible if the product can apply all relevant
 * material dimensions. Wood-less products skip the wood check entirely.
 */
export function isThemeCompatibleWithProduct(
	def: MaterialThemeDefinition,
	opts: MaterialOptions,
): boolean {
	const resolvedMetal = resolveProjectedMetal(def.metal, opts);
	if (!resolvedMetal) {
		return false;
	}
	if (opts.wood.length > 0) {
		if (def.wood === undefined || !opts.wood.includes(def.wood)) {
			return false;
		}
	}
	// Wood-less products: skip wood check (irrelevant dimension)
	if (isComboExcluded(opts, def.wood, resolvedMetal)) {
		return false;
	}
	return true;
}

/**
 * Like getValidThemes but uses lenient compatibility. Returns themes
 * compatible with ALL active products (for master domain computation).
 */
export function getCompatibleThemes(
	activeProducts: MaterialOptions[],
): MaterialTheme[] {
	const compatible: MaterialTheme[] = ["theme-none"];
	for (const theme of materialThemeUniverse) {
		if (theme === "theme-none") {
			continue;
		}
		const def = materialThemes[theme];
		let compatibleForAll = true;
		for (const opts of activeProducts) {
			if (!isThemeCompatibleWithProduct(def, opts)) {
				compatibleForAll = false;
				break;
			}
		}
		if (compatibleForAll) {
			compatible.push(theme);
		}
	}
	return compatible;
}

/**
 * Maps a master theme to the best matching representable theme for a product.
 * Used when propagating master theme to wood-less products that can't fully
 * represent the master theme but can apply its metal part.
 *
 * Returns undefined if the product can't apply the master's metal at all.
 */
// ---------------------------------------------------------------------------
// Stock-aware filtering — mutable ref injected into rules
// ---------------------------------------------------------------------------

export type StockDomains = Record<string, MaterialTheme[]>;

export type StockRef = { current: StockDomains | null };

export const defaultStockRef: StockRef = { current: null };

/**
 * Filter themes by stock availability for a product type.
 * Always keeps "theme-none". Returns all themes if stockRef is null (no stock data).
 */
export function filterByStock(
	themes: MaterialTheme[],
	productType: string,
	stockRef: StockRef,
): MaterialTheme[] {
	if (!stockRef.current) {
		return themes;
	}
	const inStock = stockRef.current[productType];
	if (!inStock) {
		return themes;
	}
	const result: MaterialTheme[] = [];
	for (const t of themes) {
		if (t === "theme-none" || inStock.includes(t)) {
			result.push(t);
		}
	}
	return result;
}

/**
 * Check if a single theme is in stock for a product type.
 * theme-none is always in stock. Returns true if no stock data.
 */
export function isThemeInStock(
	theme: MaterialTheme,
	productType: string,
	stockRef: StockRef,
): boolean {
	if (theme === "theme-none") {
		return true;
	}
	if (!stockRef.current) {
		return true;
	}
	const inStock = stockRef.current[productType];
	return !inStock || inStock.includes(theme);
}

/**
 * Check if a product type has at least one in-stock theme.
 * Returns true if no stock data.
 */
export function isProductInStock(
	productType: string,
	stockRef: StockRef,
): boolean {
	if (!stockRef.current) {
		return true;
	}
	const inStock = stockRef.current[productType];
	return !inStock || inStock.length > 0;
}

/**
 * Filter seating types to only those with at least one in-stock theme.
 * Returns all if no stock data.
 */
export function filterProductsByStock<T extends string>(
	products: T[],
	stockRef: StockRef,
): T[] {
	if (!stockRef.current) {
		return products;
	}
	const result: T[] = [];
	for (const p of products) {
		if (isProductInStock(p, stockRef)) {
			result.push(p);
		}
	}
	return result;
}

export function projectThemeToProduct(
	masterTheme: MaterialTheme,
	productOpts: MaterialOptions,
): MaterialTheme | undefined {
	if (masterTheme === "theme-none") {
		return "theme-none";
	}
	const masterDef = materialThemes[masterTheme];
	// Product must support the master's metal (directly or via projection)
	const resolvedMetal = resolveProjectedMetal(masterDef.metal, productOpts);
	if (!resolvedMetal) {
		return undefined;
	}
	// Get the product's representable themes
	const representable = getValidThemes([productOpts]);
	// Find the first theme whose metal matches the resolved metal
	// (and wood matches too, if both product and master have wood)
	for (const candidate of representable) {
		if (candidate === "theme-none") {
			continue;
		}
		const candidateDef = materialThemes[candidate];
		if (candidateDef.metal !== resolvedMetal) {
			continue;
		}
		if (
			productOpts.wood.length > 0 &&
			masterDef.wood !== undefined &&
			candidateDef.wood !== masterDef.wood
		) {
			continue;
		}
		return candidate;
	}
	return undefined;
}
